import { Link } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Building2,
  CalendarClock,
  CalendarX,
  Check,
  ChevronDown,
  ClipboardList,
  FileClock,
  FileSignature,
  FileText,
  Inbox,
  ListChecks,
  Mail,
  MessageCircle,
  Moon,
  Receipt,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Sparkles,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TRADES } from '@/lib/trade';
import { isLocale, setLocale } from '@/lib/i18n';
import { IconChip, type Tone } from '@/components/ui';
import { PublicShell, rich, VALUES } from './legal-pages';
import { HeroVisual } from './landing-visual';
import { RoiCalculator } from './roi-calculator';
import { PricingSection } from './pricing-section';

/* ---------------------------------------------------------------------------
   דף המכירה — מה שרואה מי שנכנס ל-craftmind-ai.com בלי להיות מחובר: ההבטחה,
   הבעיות והפתרון לכל אחת, המוצר לפי קהל, ההחזר (מיידי וארוך טווח + מחשבון),
   המחירים (pricing-section.tsx, מהקטלוג), איך מתחילים, אמון ושאלות. ברירת המחדל עברית (בניגוד לדפי המסמכים — ראו
   PublicShell), ומעבר השפה בכותרת מחליף.

   כנות: אין כאן לקוחות, המלצות, לוגואים או נתונים מומצאים. מספרי ההחזר הם
   הערכה מהנחות גלויות שאפשר לשנות (roi.ts). המוצר מתואר כמו שהוא באמת — גם
   בשביל Google (דף הבית של אפליקציית ה-OAuth).

   הטקסט ב-locales/{he,en}/public.json תחת landing.*.
   --------------------------------------------------------------------------- */

const SERVICE_ICONS: LucideIcon[] = [
  Mail,
  CalendarClock,
  FileSignature,
  MessageCircle,
  Smartphone,
  Receipt,
];
const SERVICE_TONES: Tone[] = ['accent', 'violet', 'teal', 'success', 'orange', 'pink'];
const OFFICE_ICONS: LucideIcon[] = [Truck, ShoppingCart, FileText, FileClock, ClipboardList];
const OFFICE_TONES: Tone[] = ['teal', 'orange', 'accent', 'violet', 'pink'];
const PROBLEM_ICONS: LucideIcon[] = [Inbox, FileSignature, Wallet, CalendarX, Moon];
const SOLUTION_ICONS: LucideIcon[] = [ListChecks, BadgeCheck, Receipt, CalendarClock, Sparkles];
const IMMEDIATE_ICONS: LucideIcon[] = [Inbox, Zap, MessageCircle, CalendarClock];
const LONG_ICONS: LucideIcon[] = [TrendingUp, Wallet, Users, BarChart3, Building2];

interface Item {
  title: string;
  body: string;
}
interface Faq {
  q: string;
  a: string;
}
interface Problem {
  problem: string;
  problemBody: string;
  solution: string;
  solutionBody: string;
}

/**
 * ?lang=en / ?lang=he — לקישורים משיווק (וצילומי מסך). נשמר כמו בחירה בכותרת.
 * בהשהיה — כדי שכל useTranslation בעץ כבר יהיה רשום לשינוי השפה (ראו
 * useVisitorLanguage ב-legal-pages).
 */
function useLangParam() {
  useEffect(() => {
    const lang = new URLSearchParams(window.location.search).get('lang');
    if (!isLocale(lang)) return;
    const id = setTimeout(() => setLocale(lang, { reload: false }), 0);
    return () => clearTimeout(id);
  }, []);
}

const BTN =
  'inline-flex h-12 items-center justify-center gap-2 rounded-(--radius-md) px-6 text-base font-semibold transition-[filter,background-color] duration-(--duration-fast)';

/* onDark — על רקע הנייבי: כפתור בתכלת של הלוגו עם טקסט נייבי (7:1). */
function Ctas({ onDark }: { onDark?: boolean }) {
  const { t } = useTranslation('public');
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      <Link
        to="/onboarding"
        className={
          onDark
            ? `${BTN} bg-accent-bright text-nav shadow-lg shadow-accent-bright/25 hover:brightness-110 focus-visible:outline-accent-bright`
            : `${BTN} bg-accent text-fg-on-accent shadow-md shadow-accent/25 hover:bg-accent-hover`
        }
      >
        {t('landing.ctaPrimary')}
        <ArrowRight className="size-4 rtl:-scale-x-100" aria-hidden />
      </Link>
      <Link
        to="/login"
        search={{}}
        className={
          onDark
            ? `${BTN} text-nav-fg ring-1 ring-nav-fg/30 hover:bg-white/10 focus-visible:outline-accent-bright`
            : `${BTN} bg-surface text-fg ring-1 ring-border-strong hover:bg-surface-hover`
        }
      >
        {t('landing.signIn')}
      </Link>
    </div>
  );
}

function SectionHeader({
  kicker,
  title,
  subtitle,
  id,
  center,
}: {
  kicker: string;
  title: string;
  subtitle?: string;
  id: string;
  center?: boolean;
}) {
  return (
    <header className={`max-w-2xl space-y-3 ${center ? 'mx-auto text-center' : ''}`}>
      <p className="text-xs font-semibold ltr:uppercase ltr:tracking-widest text-accent">
        {kicker}
      </p>
      <h2 id={id} className="text-3xl font-bold tracking-tight text-fg sm:text-4xl">
        {title}
      </h2>
      {subtitle ? <p className="text-lg leading-relaxed text-fg-muted">{subtitle}</p> : null}
    </header>
  );
}

function FeatureCard({ icon, tone, item }: { icon: LucideIcon; tone: Tone; item: Item }) {
  return (
    <li className="flex gap-4 rounded-(--radius-lg) border border-border bg-surface-raised p-5 shadow-sm transition-shadow duration-(--duration-fast) hover:shadow-md">
      <IconChip icon={icon} tone={tone} size="lg" />
      <div className="space-y-1">
        <h4 className="font-semibold text-fg">{item.title}</h4>
        <p className="text-sm leading-relaxed text-fg-muted">{item.body}</p>
      </div>
    </li>
  );
}

function Section({
  id,
  labelledBy,
  className = '',
  children,
}: {
  id?: string;
  labelledBy: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={labelledBy} className={`scroll-mt-20 ${className}`}>
      {children}
    </section>
  );
}

export function LandingPage() {
  const { t } = useTranslation('public');
  useLangParam();
  const heroPoints = t('landing.heroPoints', { returnObjects: true });
  const problems = t('landing.problems.items', { returnObjects: true }) as Problem[];
  const service = t('landing.features', { returnObjects: true }) as Item[];
  const office = t('landing.officeFeatures', { returnObjects: true }) as Item[];
  const immediate = t('landing.roi.immediate', { returnObjects: true }) as Item[];
  const long = t('landing.roi.long', { returnObjects: true }) as Item[];
  const steps = t('landing.steps', { returnObjects: true }) as Item[];
  const trust = t('landing.trust', { returnObjects: true });
  const faq = t('landing.faq', { returnObjects: true }) as Faq[];
  const trades = Object.values(TRADES).map((x) => x.label);

  return (
    <PublicShell wide>
      <main>
        {/* ---------------- hero ---------------- */}
        <section
          aria-labelledby="hero-title"
          className="relative isolate overflow-hidden bg-nav text-nav-fg"
        >
          {/* זוהר דקורטיבי בכחול, בסגול ובתכלת של המותג. */}
          <span
            className="pointer-events-none absolute -top-32 end-[-10%] -z-10 size-[30rem] rounded-full bg-accent/45 blur-3xl"
            aria-hidden
          />
          <span
            className="pointer-events-none absolute -bottom-40 start-[-8%] -z-10 size-[24rem] rounded-full bg-accent-bright/20 blur-3xl"
            aria-hidden
          />
          <span
            className="pointer-events-none absolute top-1/3 start-1/3 -z-10 size-[18rem] rounded-full bg-violet/25 blur-3xl"
            aria-hidden
          />
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-20 pt-14 sm:pt-20 lg:grid-cols-2 lg:gap-16 lg:pb-24">
            <div className="space-y-6">
              <p className="text-xs font-semibold ltr:uppercase ltr:tracking-widest text-accent-bright">
                {t('landing.kicker')}
              </p>
              <h1
                id="hero-title"
                className="text-4xl font-bold leading-tight tracking-tight text-nav-fg sm:text-5xl"
              >
                {t('landing.title')}
              </h1>
              <p className="max-w-xl text-lg leading-relaxed text-nav-fg-muted">
                {t('landing.subtitle', VALUES)}
              </p>
              <Ctas onDark />
              <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-nav-fg-muted">
                {heroPoints.map((p) => (
                  <li key={p} className="flex items-center gap-1.5">
                    <Check className="size-4 text-accent-bright" aria-hidden />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:px-6">
              <HeroVisual />
            </div>
          </div>
          <span className="bg-brand-gradient absolute inset-x-0 bottom-0 h-1" aria-hidden />
        </section>

        {/* ---------------- problems → solutions ---------------- */}
        <Section
          id="problems"
          labelledBy="problems-title"
          className="mx-auto max-w-6xl space-y-10 px-5 py-20"
        >
          <SectionHeader
            id="problems-title"
            kicker={t('landing.problems.kicker')}
            title={t('landing.problems.title')}
            subtitle={t('landing.problems.subtitle')}
          />
          <ol className="space-y-4">
            {problems.map((p, i) => (
              <li
                key={p.problem}
                className="grid items-stretch gap-3 md:grid-cols-[1fr_auto_1fr] md:gap-4"
              >
                <div className="flex gap-4 rounded-(--radius-lg) border border-danger-border/70 bg-danger-subtle/60 p-5">
                  <IconChip icon={PROBLEM_ICONS[i] ?? Inbox} tone="danger" size="lg" />
                  <div className="space-y-1">
                    <p className="text-2xs font-semibold ltr:uppercase ltr:tracking-wide text-danger">
                      {t('landing.problems.problemLabel')}
                    </p>
                    <h3 className="font-semibold text-fg">{p.problem}</h3>
                    <p className="text-sm leading-relaxed text-fg-muted">{p.problemBody}</p>
                  </div>
                </div>
                <div className="flex items-center justify-center text-fg-subtle" aria-hidden>
                  <ArrowDown className="size-5 md:hidden" />
                  <ArrowRight className="hidden size-5 md:block rtl:-scale-x-100" />
                </div>
                <div className="flex gap-4 rounded-(--radius-lg) border border-success-border/70 bg-surface-raised p-5 shadow-sm">
                  <IconChip
                    icon={SOLUTION_ICONS[i] ?? Check}
                    tone="success"
                    variant="solid"
                    size="lg"
                  />
                  <div className="space-y-1">
                    <p className="text-2xs font-semibold ltr:uppercase ltr:tracking-wide text-success">
                      {t('landing.problems.solutionLabel', VALUES)}
                    </p>
                    <h3 className="font-semibold text-fg">{p.solution}</h3>
                    <p className="text-sm leading-relaxed text-fg-muted">{p.solutionBody}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        {/* ---------------- product by audience ---------------- */}
        <Section
          id="product"
          labelledBy="product-title"
          className="border-y border-border bg-surface"
        >
          <div className="mx-auto max-w-6xl space-y-12 px-5 py-20">
            <SectionHeader
              id="product-title"
              kicker={t('landing.product.kicker')}
              title={t('landing.product.title')}
              subtitle={t('landing.product.subtitle')}
            />
            <div className="grid gap-10 lg:grid-cols-2">
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <IconChip icon={Wrench} tone="accent" variant="solid" size="lg" />
                  <div>
                    <p className="text-xs font-semibold ltr:uppercase ltr:tracking-wide text-accent">
                      {t('landing.product.serviceLabel')}
                    </p>
                    <h3 className="text-xl font-semibold text-fg">
                      {t('landing.product.serviceTitle')}
                    </h3>
                  </div>
                </div>
                <ul className="grid gap-3">
                  {service.map((f, i) => (
                    <FeatureCard
                      key={f.title}
                      icon={SERVICE_ICONS[i] ?? Mail}
                      tone={SERVICE_TONES[i % SERVICE_TONES.length]!}
                      item={f}
                    />
                  ))}
                </ul>
              </div>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <IconChip icon={Building2} tone="violet" variant="solid" size="lg" />
                  <div>
                    <p className="text-xs font-semibold ltr:uppercase ltr:tracking-wide text-violet">
                      {t('landing.product.officeLabel')}
                    </p>
                    <h3 className="text-xl font-semibold text-fg">
                      {t('landing.product.officeTitle')}
                    </h3>
                  </div>
                </div>
                <ul className="grid gap-3">
                  {office.map((f, i) => (
                    <FeatureCard
                      key={f.title}
                      icon={OFFICE_ICONS[i] ?? FileText}
                      tone={OFFICE_TONES[i % OFFICE_TONES.length]!}
                      item={f}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* ---------------- ROI ---------------- */}
        <Section
          id="roi"
          labelledBy="roi-title"
          className="mx-auto max-w-6xl space-y-12 px-5 py-20"
        >
          <SectionHeader
            id="roi-title"
            kicker={t('landing.roi.kicker')}
            title={t('landing.roi.title')}
            subtitle={t('landing.roi.subtitle')}
          />
          <div className="grid gap-6 lg:grid-cols-2">
            {(
              [
                {
                  title: 'landing.roi.immediateTitle',
                  when: 'landing.roi.immediateWhen',
                  items: immediate,
                  icons: IMMEDIATE_ICONS,
                  tone: 'teal',
                },
                {
                  title: 'landing.roi.longTitle',
                  when: 'landing.roi.longWhen',
                  items: long,
                  icons: LONG_ICONS,
                  tone: 'violet',
                },
              ] as const
            ).map((col) => (
              <div
                key={col.title}
                className="space-y-4 rounded-(--radius-xl) border border-border bg-surface-raised p-6 shadow-sm"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-xl font-semibold text-fg">{t(col.title)}</h3>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
                      col.tone === 'teal'
                        ? 'bg-teal-subtle text-teal ring-teal-border/60'
                        : 'bg-violet-subtle text-violet ring-violet-border/60'
                    }`}
                  >
                    {t(col.when)}
                  </span>
                </div>
                <ul className="space-y-4">
                  {col.items.map((it, i) => (
                    <li key={it.title} className="flex gap-3">
                      <IconChip icon={col.icons[i] ?? Check} tone={col.tone} size="md" />
                      <div>
                        <h4 className="font-semibold text-fg">{it.title}</h4>
                        <p className="text-sm leading-relaxed text-fg-muted">{it.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <RoiCalculator />
          <div className="flex justify-center">
            <Ctas />
          </div>
        </Section>

        {/* ---------------- pricing ---------------- */}
        <PricingSection />

        {/* ---------------- how it works ---------------- */}
        <Section labelledBy="how-title" className="border-b border-border">
          <div className="mx-auto max-w-6xl space-y-10 px-5 py-20">
            <h2 id="how-title" className="text-3xl font-bold tracking-tight text-fg">
              {t('landing.howTitle')}
            </h2>
            <ol className="grid gap-8 sm:grid-cols-3">
              {steps.map((s, i) => (
                <li key={s.title} className="space-y-3">
                  <span className="inline-flex size-10 items-center justify-center rounded-full bg-accent text-base font-bold text-fg-on-accent shadow-md shadow-accent/25">
                    {i + 1}
                  </span>
                  <h3 className="text-lg font-semibold text-fg">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-fg-muted">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </Section>

        {/* ---------------- who + trust ---------------- */}
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-20 lg:grid-cols-2">
          <Section labelledBy="who-title" className="space-y-4">
            <h2 id="who-title" className="text-2xl font-bold text-fg">
              {t('landing.whoTitle')}
            </h2>
            <p className="text-fg-muted">{t('landing.whoBody')}</p>
            <ul className="flex flex-wrap gap-2">
              {trades.map((label) => (
                <li
                  key={label}
                  className="rounded-full border border-accent-border bg-accent-subtle px-3 py-1 text-sm font-medium text-fg"
                >
                  {label}
                </li>
              ))}
            </ul>
          </Section>
          <Section
            labelledBy="trust-title"
            className="space-y-4 rounded-(--radius-xl) border border-border bg-surface-raised p-6 shadow-sm"
          >
            <h2 id="trust-title" className="flex items-center gap-2 text-2xl font-bold text-fg">
              <ShieldCheck className="size-6 text-success" aria-hidden />
              {t('landing.trustTitle')}
            </h2>
            <ul className="space-y-2.5">
              {trust.map((line) => (
                <li key={line} className="flex gap-2 text-sm leading-relaxed text-fg">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                  {line}
                </li>
              ))}
            </ul>
            <p className="text-sm">{rich(t('landing.trustLink'))}</p>
          </Section>
        </div>

        {/* ---------------- faq ---------------- */}
        <Section id="faq" labelledBy="faq-title" className="border-t border-border bg-surface">
          <div className="mx-auto max-w-3xl space-y-6 px-5 py-20">
            <h2 id="faq-title" className="text-3xl font-bold tracking-tight text-fg">
              {t('landing.faqTitle')}
            </h2>
            <div className="divide-y divide-border rounded-(--radius-lg) border border-border bg-surface-raised">
              {faq.map((f) => (
                <details key={f.q} className="group px-5 py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-(--radius-sm) font-medium text-fg">
                    {f.q}
                    <ChevronDown
                      className="size-4 shrink-0 text-fg-subtle transition-transform group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-fg-muted">{rich(f.a)}</p>
                </details>
              ))}
            </div>
          </div>
        </Section>

        {/* ---------------- closing cta ---------------- */}
        <section
          aria-labelledby="cta-title"
          className="relative isolate overflow-hidden bg-nav text-nav-fg"
        >
          <span
            className="pointer-events-none absolute -top-24 start-1/4 -z-10 size-[26rem] rounded-full bg-accent/40 blur-3xl"
            aria-hidden
          />
          <span
            className="pointer-events-none absolute -bottom-32 end-[-5%] -z-10 size-[20rem] rounded-full bg-violet/30 blur-3xl"
            aria-hidden
          />
          <span className="bg-brand-gradient absolute inset-x-0 top-0 h-1" aria-hidden />
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 px-5 py-20 text-center">
            <h2
              id="cta-title"
              className="text-3xl font-bold tracking-tight text-nav-fg sm:text-4xl"
            >
              {t('landing.ctaTitle')}
            </h2>
            <p className="text-lg text-nav-fg-muted">{t('landing.ctaBody')}</p>
            <Ctas onDark />
          </div>
        </section>
      </main>
    </PublicShell>
  );
}
